import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ChatMessage } from '../entities/ChatMessage';
import { ChatInput } from '../types';
import { chatService, apiService } from '../services/api';
import { getLogger } from '../utils/logger';
import { useTeam } from './TeamContext';

const logger = getLogger('ChatContext');

interface ChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
}

interface ChatContextType {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearChat: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [state, setState] = useState<ChatState>({
    messages: [],
    isLoading: false,
    error: null
  });
  
  // Get team context for sending with messages
  const { getUserTeam, getOpponentTeam, matchupConfig, selectedLeague } = useTeam();
  
  // Generate a session ID for this chat session
  const [sessionId] = useState<string>(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const sendMessage = async (content: string) => {
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content,
      role: 'user',
      timestamp: new Date()
    };

    let updatedMessages: ChatMessage[] = [];
    setState(prev => {
      updatedMessages = [...prev.messages, userMessage];
      return {
        ...prev,
        messages: updatedMessages,
        isLoading: true,
        error: null
      };
    });

    try {
      logger.info('Sending chat message', { content });
      
      // Get team context
      const userTeam = getUserTeam();
      const opponentTeam = getOpponentTeam();
      const ourTeam = userTeam?.teamId;
      const opponentTeamAbbr = opponentTeam?.teamId;
      
      // Use the season from matchup config, with fallback to current season
      const apiSeason = matchupConfig?.season || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`;
      
      logger.info('Team context for chat', { 
        ourTeam, 
        opponentTeam: opponentTeamAbbr, 
        season: matchupConfig?.season, 
        apiSeason, 
        sessionId,
        matchupConfig: matchupConfig
      });
      
      // Build message history including the optimistic user message
      const historySource = updatedMessages.length > 0 ? updatedMessages : [...state.messages, userMessage];
      const history = historySource.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Build match_config
      const matchConfig = {
        stage: "regular",
        userTeam: {
          teamId: ourTeam || '',
          teamName: userTeam?.teamName || ''
        },
        opponentTeam: {
          teamId: opponentTeamAbbr || '',
          teamName: opponentTeam?.teamName || ''
        },
        seasonId: apiSeason,
        league: selectedLeague?.abbreviation || "NBA" // Use selected league or default to NBA
      };

      // Build ChatInput object
      const chatInput: ChatInput = {
        message: content,
        history,
        match_config: matchConfig
      };

      // Always initiate regular chat; only use its result if there is no QA match
      const chatPromise = chatService
        .sendMessage(chatInput)
        .catch((e: any) => ({ error: e?.message || 'Unknown error' }));

      const handleQAEventSource = async (question: string): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
          const questionId = Date.now().toString();
          let producedVisibleMessage = false;
          const lastThinkingAt: Record<number, number> = {};
          // Show typing indicator during the whole SSE session
          setState(prev => ({ ...prev, isLoading: true }));

          try {
            const es = apiService.openQAEventSource(question);
            es.onmessage = (event) => {
              try {
                const data = JSON.parse(event.data);
                if (data.type === 'thinking') {
                  if (typeof data.answer_index === 'number' && data.answer_index > 0) {
                    updateOrCreateMessage(data.content, data.answer_index, data.total_answers, questionId);
                    producedVisibleMessage = true;
                    lastThinkingAt[data.answer_index] = Date.now();
                  }
                } else if (data.type === 'answer') {
                  const idx = Number(data.answer_index ?? 0);
                  const lastT = lastThinkingAt[idx] || 0;
                  const since = Date.now() - lastT;
                  const needsDelay = lastT > 0 && since < 500;
                  const show = () => {
                    updateOrCreateMessage(data.content, idx, data.total_answers, questionId);
                    producedVisibleMessage = true;
                  };
                  if (needsDelay) {
                    setTimeout(show, 800 - since);
                  } else {
                    show();
                  }
                } else if (data.type === 'complete') {
                  es.close();
                  setState(prev => ({ ...prev, isLoading: false }));
                  resolve(producedVisibleMessage);
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            };
            es.onerror = () => {
              es.close();
              resolve(false);
            };
          } catch (e) {
            resolve(false);
          }
        });
      };

      // Only use QA stream if question is in curated Q&A
      const hasMatch = await apiService.hasQAMatch(content);
      if (hasMatch) {
        logger.info('Q&A match found, opening SSE EventSource');
        const handled = await handleQAEventSource(content);
        if (handled) {
          return; // stream handled UI; ignore chat response
        }
      }

      // No QA match (or stream produced nothing) → use regular chat result
      logger.info('Using regular chat');
      const response = await chatPromise;
      
      const r: any = response as any;
      if (r && r.error) {
        logger.error('Chat API error', { error: r.error });
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: r.error || 'Unknown error occurred'
        }));
        return;
      }

      if (r && r.response) {
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: r.response,
          role: 'assistant',
          timestamp: new Date()
        };

        setState(prev => ({
          ...prev,
          messages: [...prev.messages, assistantMessage],
          isLoading: false
        }));
        
        logger.info('Chat message sent successfully', { responseLength: r.response.length });
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'No response received from server'
        }));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Failed to send chat message', { error: errorMessage });
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
    } finally {
      // Safety net to avoid getting stuck in loading state
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };


  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleQAStream = async (stream: ReadableStream<Uint8Array>): Promise<boolean> => {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    const questionId = Date.now().toString(); // Unique ID for this question
    let producedVisibleMessage = false;
    const lastThinkingAt: Record<number, number> = {};

    // Show typing indicator for the entire QA stream
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'thinking') {
                // Show thinking animation only from the second answer onward
                if (typeof data.answer_index === 'number' && data.answer_index > 0) {
                  updateOrCreateMessage(data.content, data.answer_index, data.total_answers, questionId);
                  producedVisibleMessage = true;
                  lastThinkingAt[data.answer_index] = Date.now();
                }
              } else if (data.type === 'answer') {
                // Replace thinking with actual answer
                const idx = Number(data.answer_index ?? 0);
                const lastT = lastThinkingAt[idx] || 0;
                const since = Date.now() - lastT;
                const needsDelay = lastT > 0 && since < 500; // if buffered, thinking and answer arrive together
                // eslint-disable-next-line no-loop-func
                const show = () => {
                  updateOrCreateMessage(data.content, idx, data.total_answers, questionId);
                  producedVisibleMessage = true;
                };
                if (needsDelay) {
                  setTimeout(show, 800 - since);
                } else {
                  show();
                }
              } else if (data.type === 'complete') {
                setState(prev => ({ ...prev, isLoading: false }));
                return producedVisibleMessage;
              }
            } catch (e) {
              console.error('Error parsing stream data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error reading stream:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Error reading stream response'
      }));
      return false;
    } finally {
      reader.releaseLock();
    }
    return producedVisibleMessage;
  };

  const updateOrCreateMessage = (content: string, answerIndex: number, totalAnswers: number, questionId: string) => {
    setState(prev => {
      const messageId = `qa-${questionId}-${answerIndex}`;
      const existingMessageIndex = prev.messages.findIndex(msg => msg.id === messageId);
      
      const message: ChatMessage = {
        id: messageId,
        content,
        role: 'assistant',
        timestamp: new Date() // Always update timestamp to force re-render
      };

      if (existingMessageIndex >= 0) {
        // Update existing message (replace thinking with answer)
        const newMessages = [...prev.messages];
        newMessages[existingMessageIndex] = {
          ...message,
          timestamp: new Date() // Force React to detect the change
        };
        return {
          ...prev,
          messages: newMessages
        };
      } else {
        // Create new message for this answer
        return {
          ...prev,
          messages: [...prev.messages, message]
        };
      }
    });
  };

  const clearChat = () => {
    logger.info('Clearing chat history');
    setState(prev => ({
      ...prev,
      messages: [],
      error: null
    }));
  };

  return (
    <ChatContext.Provider value={{
      messages: state.messages,
      isLoading: state.isLoading,
      error: state.error,
      sendMessage,
      clearChat
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}; 
